import moment from "moment";
import { PaidItem } from "@/hooks/usePaidItems";

moment.locale('pt-BR');

const createSafeDate = (dateInput: Date | string | null | undefined): moment.Moment | null => {
    if (!dateInput) return null;
    
    if (dateInput instanceof Date) {
        return moment(dateInput);
    }
    
    const dateStr = String(dateInput);
    
    let parsedDate = moment(dateStr, 'YYYY-MM-DD', true);
    if (parsedDate.isValid()) {
        return parsedDate;
    }
    
    parsedDate = moment(dateStr, 'YYYY-DD-MM', true);
    if (parsedDate.isValid()) {
        return parsedDate;
    }
    
    parsedDate = moment(dateStr);
    if (parsedDate.isValid()) {
        return parsedDate;
    }
    
    return null;
};

export interface Debts {
    sqPassagemAvulsa: number;
    dataPassagem: Date;
    idPassagemFreeFlow: number;
    valorPassagem: number;
    vlMulta: number;
    nmPraca: string;
    nmConcessionaria: string;
    vlTotalPagar: number;
    dtVencimento: Date;
    placa: string;
    fmtDataPassagem?: string;
}

export interface FormattedDebts {
    id: string;
    date: string;
    hour: string;
    price: string;
    originalPrice: string;
    dueDate: string;
    daysLeft: number;
    original: Debts;
    hasDate: boolean;
    plate: string;
    fmtDataPassagem?: string;
    nmConcessionaria: string;
}

const getSortedDebts: (debts: Debts[]) => Debts[] = (debts) => {
    return debts?.sort((a, b) => {
        const dateA = moment(a?.dtVencimento);
        const dateB = moment(b?.dtVencimento);
        return dateA.diff(dateB);
    });
}

export const getFormattedDebts: (debts: Debts[]) => FormattedDebts[] = (debts) => {
    const now = moment();
    
    return getSortedDebts(debts)?.map(item => {
        const hasDate = !!item?.dtVencimento;

        const date = moment(item?.dataPassagem);
        const dueDate = moment(item?.dtVencimento);

        const formattedDate = date.format('DD/MM/YYYY');
        const formattedHour = date.format('HH:mm:ss');
        const daysLeft = now.diff(dueDate, 'days');

        const price = item?.vlTotalPagar?.toFixed(2).replace(/\./g, ',');
        const originalPrice = item?.valorPassagem?.toFixed(2).replace(/\./g, ',');

        return {
            id: item?.sqPassagemAvulsa?.toString(),
            date: formattedDate,
            hour: formattedHour,
            dueDate: dueDate.format('DD/MM/YYYY'),
            daysLeft,
            price,
            originalPrice,
            original: item,
            hasDate,
            plate: item.placa,
            fmtDataPassagem: item?.fmtDataPassagem,
            nmConcessionaria: item.nmConcessionaria
        };
    }) || [];
};

export interface Tickets {
    sqPagamento: number;
    dataPagamento: Date;
    valorPassagem: number;
    status: string;
    placaVeiculo: string;
}

interface FormattedTickets {
    id: string;
    date: string;
    price: string;
    status: string;
    plate: string;
    fullDate: Date | null;
}

const getRemovedDuplicatesById: (tickets: Tickets[]) => Tickets[] = (tickets) => {
  const seen = new Set();
  return tickets.filter(item => {
    if (seen.has(item.sqPagamento)) return false;
    seen.add(item.sqPagamento);
    return true;
  });
}

const getSortedTickets: (tickets: Tickets[]) => Tickets[] = (tickets) => {
    return tickets?.sort((a, b) => {
        const dateA = moment(a?.dataPagamento);
        const dateB = moment(b?.dataPagamento);
        return dateB.diff(dateA);
    });
}

export const getFormattedTickets = (tickets: Tickets[]) => {
    if (!tickets) return [];

    const filteredTickets = tickets?.filter(item => item?.status?.toLocaleLowerCase()?.includes("processamento"))

    const unifiedTickets = getRemovedDuplicatesById(filteredTickets);

    const sorted = getSortedTickets(unifiedTickets);

    const formattedTickets: FormattedTickets[] = sorted?.map(item => {
        const date = createSafeDate(item?.dataPagamento);
        const formattedDate = date?.format('DD/MM/YYYY');
        return {
            id: String(item?.sqPagamento ?? 0),
            date: formattedDate ?? "",
            fullDate: date?.toDate() ?? null,
            price: item?.valorPassagem?.toFixed(2)?.replace(/\./g, ','),
            status: item.status,
            plate: item.placaVeiculo
        }
    });

    return formattedTickets;
};

export interface DebtsByInvoice {
    sqPagamento: number;
    valorPassagem: number;
    dataTransacao: Date;
    nmConcessionaria: string;
    placa: string;
    vlTotalPagar: number;
    valorMulta: number;
}

interface FormattedDebtsByInvoice {
    plate: string;
    date: string;
    price: string;
    owner: string;
    originalPrice: string;
    fine: string;
}

export const getFormattedDebtsByInvoice = (debts: DebtsByInvoice[]) => {
    if (!debts) return [];

    const formattedTickets: FormattedDebtsByInvoice[] = debts?.map(item => {
        const date = createSafeDate(item?.dataTransacao);
        const formattedDate = date?.format('DD/MM/YYYY');
        return {
            id: String(item?.sqPagamento ?? 0),
            date: formattedDate ?? "",
            price: item?.vlTotalPagar?.toFixed(2)?.replace(/\./g, ','),
            plate: item.placa,
            owner: item.nmConcessionaria,
            originalPrice: item?.valorPassagem?.toFixed(2)?.replace(/\./g, ','),
            fine: item?.valorMulta?.toFixed(2)?.replace(/\./g, ','),
        }
    });

    return formattedTickets;
};

interface FormattedPaidItem {
    id: string;
    date: string;
    price: string;
    status: string;
    plate: string;
    nmPraca: string;
    nmConcessionaria: string;
}

const getRemovedDuplicatesPaidItemsById: (items: PaidItem[]) => PaidItem[] = (items) => {
    const seen = new Set();
    return items.filter(item => {
        if (seen.has(item.idPassagemFreeFlow)) return false;
        seen.add(item.idPassagemFreeFlow);
        return true;
    });
}

const getSortedPaidItems: (items: PaidItem[]) => PaidItem[] = (items) => {
    return items?.sort((a, b) => {
        const dateA = moment(a?.dtPagamento);
        const dateB = moment(b?.dtPagamento);
        return dateB.diff(dateA);
    });
}

export const getFormattedPaidItems = (items: PaidItem[]) => {
    if (!items) return [];

    const filteredItems = items?.filter(item => 
        item?.statusTransacao?.toLowerCase()?.includes("processamento")
    );

    const unifiedItems = getRemovedDuplicatesPaidItemsById(filteredItems);

    const sorted = getSortedPaidItems(unifiedItems);

    const formattedItems: FormattedPaidItem[] = sorted?.map(item => {
        return {
            id: String(item?.idPassagemFreeFlow ?? 0),
            date: item.fmtDataPassagem,
            price: item?.vlTotalPagar?.toFixed(2)?.replace(/\./g, ','),
            status: item.statusTransacao,
            plate: item.placaVeiculo,
            nmPraca: item.nmPraca,
            nmConcessionaria: item.nmConcessionaria
        }
    });

    return formattedItems;
};